import {
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  SelectChangeEvent,
  TextField,
  Typography,
} from '@mui/material'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Product,
  ProductDimension,
} from '../../../modules/productsListing/types'
import { useStyles } from './Dimensions.styles'
import InfoOutlined from '@mui/icons-material/InfoOutlined'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'

import {
  addDimensionRowOnFreeTrial,
  addProductOnFreeTrial,
  removeDimensionRowOnFreeTrial,
  updateDimensionRowIdOnFreeTrial,
  updateDimensionRowQuantityOnFreeTrial,
  updateNumberOfDaysOnFreeTrial,
} from '../../../modules/freeTrials/action'
import { useDispatch, useSelector } from '../../../store/hooks'
import { ProductDimensionFreeTrial } from '../../../modules/freeTrials/types'
import clsx from 'clsx'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import HighlightOffIcon from '@mui/icons-material/HighlightOff'
import { debounce, get, isEmpty } from 'lodash'
import { LabraToolTip, PlanType } from '@labrav/react-components'
import { flyoutFormDebounceTime } from '../../../common/utils/constants'
import { getRandom, preventEventForKeys } from '@labrav/common'
import { Price, PriceTypes } from '../../../modules/privateOffer/types'
import { useCommonStyles } from '../../../common/styles/common.styles'
import { priceMapping } from './Dimensions.constants'

export { priceMapping, priceMappingWithAll } from './Dimensions.constants'

export type DimensionsType = {
  product: Product
}

export const Dimensions = React.memo(({ product }: DimensionsType) => {
  const dispatch = useDispatch()
  const { productDimensionsFreeTrials, expiresInDays } = useSelector<{
    productDimensionsFreeTrials: ProductDimensionFreeTrial[]
    expiresInDays: number | null
  }>(state => {
    return {
      productDimensionsFreeTrials:
        state.freeTrials[product.productId]?.productDimensions || [],
      expiresInDays:
        state.freeTrials[product.productId]?.freeTrial
          ?.freeTrialExpiresInDays || null,
    }
  })
  const productDimensions =
    product.productType === PlanType.SAAS_CONTRACT
      ? product?.productDimensions?.filter(
          dimension => !dimension.isAdditionalUsageAllowed
        )
      : product.productDimensions
  const freeTrialIds = productDimensionsFreeTrials
    .map(dimension => dimension.productDimensionId || '')
    .filter(id => !isEmpty(id))

  const noOp = useCallback(() => void 0, [])

  useEffect(() => {
    dispatch(addProductOnFreeTrial(product.productId))
  }, [product])
  const addDimensionRow = useCallback(async () => {
    await dispatch(addDimensionRowOnFreeTrial(product.productId))
  }, [product])

  const updateId = useCallback(
    (index: number) => async (event: SelectChangeEvent) => {
      await dispatch(
        updateDimensionRowIdOnFreeTrial(
          product.productId,
          index,
          event.target.value as string
        )
      )
    },
    [product]
  )

  const updateQuantity = useCallback(
    (index: number) =>
      debounce(async (event: React.ChangeEvent<HTMLInputElement>) => {
        await dispatch(
          updateDimensionRowQuantityOnFreeTrial(
            product.productId,
            index,
            event.target.value as unknown as number
          )
        )
      }, flyoutFormDebounceTime),
    [product]
  )

  const updateNumberOfDays = useCallback(
    debounce(async (event: React.ChangeEvent<HTMLInputElement>) => {
      await dispatch(
        updateNumberOfDaysOnFreeTrial(
          product.productId,
          event.target.value as unknown as number
        )
      )
    }, flyoutFormDebounceTime),
    [product]
  )

  const deleteRow = useCallback(
    (index: number) => async () => {
      await dispatch(removeDimensionRowOnFreeTrial(product.productId, index))
    },
    [product]
  )
  const { classes } = useStyles()
  const disableAddDimension =
    productDimensions &&
    productDimensions.length === productDimensionsFreeTrials.length
  const expiringInDays =
    expiresInDays !== null && (expiresInDays < 7 || expiresInDays > 90)
  return (
    <div className={classes.root} data-testid="dimensions-container">
      <div className={classes.dimensionsHeader}>
        <Typography className={classes.heading}>Dimensions</Typography>
      </div>
      <div className={classes.dimensionsBody}>
        <div className={classes.subHeadingContainer}>
          <Typography className={classes.subHeading}>
            Choose Dimensions
          </Typography>
          <LabraToolTip
            tooltipPlacement={'right'}
            label={'Choose the dimensions you want to offer under free trial.'}
          >
            <InfoOutlined className={classes.infoIcon} />
          </LabraToolTip>
        </div>
        {productDimensionsFreeTrials.map((freeTrialDimension, index) => (
          <DimensionRows
            index={index}
            offerDimension={{
              id: freeTrialDimension?.productDimensionId || '',
              quantity: freeTrialDimension.freeTrial?.quantity,
            }}
            productDimensions={productDimensions || []}
            updateId={updateId(index)}
            updateQuantity={updateQuantity(index)}
            deleteRow={deleteRow(index)}
            offerIds={freeTrialIds}
            isFreeTrial={true}
            errors={{
              qauntity: isEmpty(freeTrialDimension.freeTrial?.quantity)
                ? false
                : Number(freeTrialDimension.freeTrial?.quantity) === 0,
            }}
          />
        ))}
        <div
          className={clsx(
            classes.addDimensionContainer,
            disableAddDimension && classes.addDimensionsDisabled
          )}
          onClick={disableAddDimension ? noOp : addDimensionRow}
          data-testid="add-dimension"
        >
          <AddCircleOutlineIcon className={classes.addIcon} />
          <Typography className={classes.addText}>Add dimension</Typography>
        </div>
        <Divider className={classes.divider} />
        <div className={classes.numberOfDaysContainer}>
          <TextField
            id="noOfDays"
            label="Free Trial Duration (No of days)"
            variant="standard"
            defaultValue={expiresInDays}
            className={classes.numberOfDaysField}
            required={true}
            onChange={updateNumberOfDays}
            type="number"
            error={expiringInDays}
            helperText={
              expiringInDays ? 'Number of Days should be between 7 and 90.' : ''
            }
            onKeyDown={event =>
              event.key === '-' ||
              event.key === '+' ||
              event.key === '.' ||
              event.key === 'e'
                ? event.preventDefault()
                : null
            }
            slotProps={{
              input: {
                inputProps: { min: 7, max: 90, 'data-testid': 'numberOfDays' },
              },
            }}
          />
        </div>
      </div>
    </div>
  )
})

export interface DimensionsData {
  quantity?: number
  id: string
  price?: Price
}
export type DimensionRowsProps = {
  offerDimension: DimensionsData
  offerIds: string[]
  pricesArray?: [keyof PriceTypes]
  productDimensions: ProductDimension[]
  updateId: (event: SelectChangeEvent, child: React.ReactNode) => void
  updateQuantity: (event: React.ChangeEvent<HTMLInputElement>) => void
  deleteRow: () => void
  index: number
  isCustom?: boolean
  isFlexiblePayment?: boolean
  isFreeTrial?: boolean
  errors: Record<string, unknown>
}

export const DimensionRows = React.memo(
  ({
    index,
    isCustom,
    offerDimension,
    productDimensions,
    updateId,
    updateQuantity,
    deleteRow,
    offerIds,
    errors,
    isFlexiblePayment = true,
    pricesArray = ['price'],
  }: DimensionRowsProps) => {
    const { classes } = useStyles()
    const { classes: commonClasses } = useCommonStyles()
    const [quantityValue, setQuantityValue] = useState(
      offerDimension?.quantity ?? ''
    )

    const onHandleChangeQuantity = (
      event: React.ChangeEvent<HTMLInputElement>
    ) => {
      const dataValue = `${Math.round(Number(event.target.value))}`
      setQuantityValue(dataValue)
      updateQuantity({
        ...event,
        target: {
          ...event.target,
          value: dataValue,
          id: event.target.id,
        },
      })
    }
    const dimension = productDimensions.find(
      dimension => dimension.id === offerDimension.id
    )
    const mappings: Record<string, string> = useMemo(() => {
      if (isCustom) {
        return { ...priceMapping, price: 'Custom price' }
      } else {
        return { ...priceMapping, price: 'Price' }
      }
    }, [isCustom])
    const randomKey = useMemo(getRandom, [
      index,
      offerIds.length,
      offerDimension.id,
    ])
    return (
      <div
        className={classes.row}
        data-testid="dimension-row"
        key={`${randomKey}-${index}`}
      >
        <FormControl className={classes.formWidth}>
          <InputLabel
            id="product-dimension-label"
            className={classes.inputLabel}
            classes={{ shrink: commonClasses.shrink }}
          >
            Name
          </InputLabel>
          <Select
            labelId="product-dimension"
            defaultValue={dimension?.id ?? ''}
            onChange={updateId}
            label="Name"
            inputProps={{ 'data-testid': 'input' }}
            className={classes.selectField}
            variant="standard"
            IconComponent={props => (
              <KeyboardArrowDownIcon
                {...props}
                fontSize="small"
                data-testid="arrow-button"
              />
            )}
            classes={{ icon: classes.icon }}
            data-testid="dimension-name"
          >
            {productDimensions.map(dimension => (
              <MenuItem
                data-testid="menu-item"
                value={dimension.id}
                className={classes.menuRoot}
                disabled={offerIds.includes(dimension?.id || '')}
                key={dimension.id}
              >
                {dimension.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          id="description"
          label="Description"
          variant="standard"
          value={dimension?.labelDescription || '--'}
          className={clsx(classes.selectField, classes.description)}
          data-testid="description"
          multiline
          slotProps={{
            input: {
              readOnly: true,
              disableUnderline: true,
            },
          }}
        />
        <div
          className={clsx(classes.quantityFieldContainer, classes.textField)}
        >
          {isFlexiblePayment ? (
            <TextField
              key="quantity"
              id="quantity"
              name="quantity"
              label="QUANTITY"
              variant="standard"
              required={true}
              defaultValue={quantityValue}
              value={quantityValue}
              className={clsx(classes.selectField, classes.textField)}
              disabled={isEmpty(dimension?.id)}
              onChange={onHandleChangeQuantity}
              type="number"
              onKeyDown={preventEventForKeys(['+', '-', '.', 'e'])}
              error={!isEmpty(errors?.quantity)}
              helperText={(errors?.quantity as string) || ''}
              slotProps={{
                input: { inputProps: { min: 1, 'data-testid': 'quantity' } },
              }}
            />
          ) : (
            pricesArray.map(price => (
              <TextField
                key={price}
                id={price}
                name={price}
                label={mappings[price]}
                variant="standard"
                required={true}
                defaultValue={
                  get(offerDimension, `price.${price}`, null) ?? null
                }
                className={clsx(
                  classes.selectField,
                  classes.priceFieldsWidthAndSpacing
                )}
                disabled={isEmpty(dimension?.id)}
                onChange={updateQuantity}
                type="number"
                onKeyDown={preventEventForKeys(['+', '-', 'e'])}
                error={!isEmpty(errors?.[price])}
                helperText={(errors?.[price] as string) || ''}
                slotProps={{
                  input: {
                    inputProps: { min: 1, 'data-testid': `price-${price}` },
                  },
                }}
              />
            ))
          )}
          <IconButton
            className={classes.iconButton}
            onClick={deleteRow}
            data-testid="cancel-button"
            size="large"
          >
            <HighlightOffIcon className={classes.closeIcon} />
          </IconButton>
        </div>
      </div>
    )
  }
)
